/**
 * Create-user wizard with explicit separation:
 * 1. Сотрудник
 * 2. Роль в платформе
 * 3. Организация
 * 4. Группы доступа и reminder про RLS
 */
import { useState, useMemo, useCallback } from 'react'
import {
  Drawer,
  Steps,
  Form,
  Input,
  Select,
  Button,
  Space,
  Result,
  Typography,
  Alert,
  Spin,
  message,
} from 'antd'
import {
  UserOutlined,
  BankOutlined,
  SafetyOutlined,
  TeamOutlined,
  CheckCircleOutlined,
  CopyOutlined,
  PlusOutlined,
} from '@ant-design/icons'

import { useAuthStore } from '@/store/auth'
import { useCreateUser, useOrganizations } from '@/hooks/useUsers'
import { useAccessGroups, useAddMember } from '@/hooks/useAccessGroups'
import type { OrganizationItem } from '@/api/users'
import { isServiceAccessGroup } from '@/utils/accessGroups'
import {
  ROLE_DESCRIPTIONS,
  ROLE_LABELS,
  getAssignableRoles,
} from '@/utils/userRoles'

const { Text, Paragraph } = Typography

// ── Helpers ──

function generateTempPassword(length = 12): string {
  const uppercase = 'ABCDEFGHJKLMNPQRSTUVWXYZ'
  const lowercase = 'abcdefghjkmnpqrstuvwxyz'
  const digits = '23456789'
  const specials = '!@#$'
  const all = uppercase + lowercase + digits + specials

  const rand = (set: string) => set[crypto.getRandomValues(new Uint8Array(1))[0] % set.length]

  const result: string[] = [rand(uppercase), rand(lowercase), rand(digits), rand(specials)]

  const array = new Uint8Array(length - 4)
  crypto.getRandomValues(array)
  for (let i = 0; i < length - 4; i++) {
    result.push(all[array[i] % all.length])
  }

  // Fisher-Yates shuffle
  for (let i = result.length - 1; i > 0; i--) {
    const j = crypto.getRandomValues(new Uint8Array(1))[0] % (i + 1)
    ;[result[i], result[j]] = [result[j], result[i]]
  }

  return result.join('')
}

function isYandexExpectedEmail(email: string): boolean {
  const normalized = email.trim().toLowerCase()
  return normalized.endsWith('@yandex.ru') || normalized.endsWith('@ya.ru')
}

// ── Types ──

interface WizardFormValues {
  full_name: string
  email: string
  org_id?: string
  role: string
  group_ids?: string[]
}

interface CreatedUserInfo {
  user_id: string
  full_name: string
  email: string
  org_name: string
  role_label: string
  group_names: string[]
  temp_password: string
  requires_yandex_linking: boolean
}

interface CreateUserWizardProps {
  open: boolean
  onClose: () => void
  onOpenUserIdentity?: (userId: string, email: string) => void
}

// ── Component ──

export default function CreateUserWizard({ open, onClose, onOpenUserIdentity }: CreateUserWizardProps) {
  const authUser = useAuthStore((s) => s.user)
  const isSuperadmin = authUser?.role === 'superadmin'
  const assignableRoles = useMemo(() => getAssignableRoles(isSuperadmin), [isSuperadmin])

  const [currentStep, setCurrentStep] = useState(0)
  const [submitting, setSubmitting] = useState(false)
  const [createdUser, setCreatedUser] = useState<CreatedUserInfo | null>(null)

  const [form] = Form.useForm<WizardFormValues>()

  // ── Data hooks ──
  const { data: organizations } = useOrganizations()
  const createUserMutation = useCreateUser()
  const addMemberMutation = useAddMember()

  // Watch form values for dynamic behavior
  const selectedRole = Form.useWatch('role', form)
  const selectedOrgId = Form.useWatch('org_id', form)

  const { data: accessGroups } = useAccessGroups(selectedOrgId)

  // ── Derived data ──
  const orgOptions = useMemo(
    () =>
      (organizations || []).map((o: OrganizationItem) => ({
        value: o.id,
        label: `${o.code} — ${o.name}`,
        name: o.name,
      })),
    [organizations],
  )

  const groupOptions = useMemo(
    () =>
      (accessGroups || [])
        .filter((g) => g.is_active && !isServiceAccessGroup(g.zitadel_role_key))
        .map((g) => ({
          value: g.id,
          label: g.name,
        })),
    [accessGroups],
  )

  // ── Step definitions ──
  const steps = [
    { title: 'Сотрудник', icon: <UserOutlined /> },
    { title: 'Роль', icon: <SafetyOutlined /> },
    { title: 'Организация', icon: <BankOutlined /> },
    { title: 'Группы', icon: <TeamOutlined /> },
  ]

  // ── Validation per step ──
  const STEP_FIELDS: string[][] = [
    ['full_name', 'email'],
    ['role'],
    ['org_id'],
    [],
  ]

  const validateCurrentStep = useCallback(async (): Promise<boolean> => {
    try {
      await form.validateFields(STEP_FIELDS[currentStep])
      return true
    } catch {
      return false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, currentStep])

  // ── Navigation ──
  const handleNext = async () => {
    if (await validateCurrentStep()) {
      setCurrentStep((s) => s + 1)
    }
  }

  const handlePrev = () => {
    setCurrentStep((s) => s - 1)
  }

  // ── Submit ──
  const handleSubmit = async () => {
    if (!(await validateCurrentStep())) return

    setSubmitting(true)
    try {
      const values = form.getFieldsValue(true) as WizardFormValues
      const tempPassword = generateTempPassword()

      // 1. Create user
      const newUser = await createUserMutation.mutateAsync({
        email: values.email,
        full_name: values.full_name,
        role: values.role,
        org_id: values.org_id,
        password: tempPassword,
      })

      // 2. Assign to groups (if selected)
      const createdGroupNames: string[] = []
      const failedGroupNames: string[] = []
      for (const groupId of values.group_ids || []) {
        const groupName = groupOptions.find((g) => g.value === groupId)?.label || groupId
        try {
          await addMemberMutation.mutateAsync({
            groupId,
            userId: newUser.id,
          })
          createdGroupNames.push(groupName)
        } catch (err: any) {
          failedGroupNames.push(groupName)
          console.error('Group assignment failed:', err)
        }
      }
      if (failedGroupNames.length > 0) {
        message.warning(
          `Сотрудник создан, но не удалось добавить в группы: ${failedGroupNames.join(', ')}.`,
        )
      }

      // 3. Resolve org name for display
      const orgName =
        orgOptions.find((o) => o.value === values.org_id)?.name || '—'

      setCreatedUser({
        user_id: newUser.id,
        full_name: values.full_name,
        email: values.email,
        org_name: orgName,
        role_label: ROLE_LABELS[values.role] || values.role,
        group_names: createdGroupNames,
        temp_password: tempPassword,
        requires_yandex_linking: isYandexExpectedEmail(values.email),
      })
    } catch {
      // createUser mutation already shows error via hook
    } finally {
      setSubmitting(false)
    }
  }

  // ── Reset for serial creation ──
  const handleCreateAnother = () => {
    setCreatedUser(null)
    setCurrentStep(0)
    form.resetFields()
    // Re-set org_id for org_admin
    if (!isSuperadmin && authUser?.orgId) {
      form.setFieldValue('org_id', authUser.orgId)
      form.setFieldValue('role', 'analyst')
    }
  }

  // ── Close & cleanup ──
  const handleClose = () => {
    setCreatedUser(null)
    setCurrentStep(0)
    form.resetFields()
    onClose()
  }

  // ── Copy password ──
  const handleCopyPassword = () => {
    if (createdUser?.temp_password) {
      navigator.clipboard.writeText(createdUser.temp_password)
      message.success('Пароль скопирован')
    }
  }

  // ── Render steps ──
  const renderStep1 = () => (
    <>
      <Form.Item
        name="full_name"
        label="ФИО"
        rules={[{ required: true, message: 'Введите ФИО сотрудника' }]}
      >
        <Input placeholder="Иванов Иван Иванович" autoFocus />
      </Form.Item>
      <Form.Item
        name="email"
        label="Email"
        rules={[
          { required: true, message: 'Введите email' },
          { type: 'email', message: 'Некорректный формат email' },
        ]}
      >
        <Input placeholder="ivanov@example.com" />
      </Form.Item>

      <Alert
        type="warning"
        showIcon
        message="Создание сотрудника не делает Yandex-вход автоматическим"
        description="Wizard создает локальный Zitadel account. Для Yandex-ready onboarding ориентируйтесь на вкладку «Логин / identity». Сейчас правило ожидаемого Yandex-входа применяется к @yandex.ru и @ya.ru; кастомные домены Yandex 360 требуют отдельного явного признака."
      />
    </>
  )

  const renderStep2 = () => (
    <>
      <Form.Item
        name="role"
        label="Роль в платформе"
        rules={[{ required: true, message: 'Выберите роль' }]}
      >
        <Select
          placeholder="Выберите роль"
          options={assignableRoles.map((role) => ({
            value: role,
            label: ROLE_LABELS[role] || role,
          }))}
        />
      </Form.Item>

      <Alert
        type="info"
        showIcon
        message="Что делает этот шаг"
        description={
          <Space direction="vertical" size={4}>
            {assignableRoles.map((role) => (
              <Text key={role}>
                <Text strong>{ROLE_LABELS[role] || role}:</Text> {ROLE_DESCRIPTIONS[role] || '—'}
              </Text>
            ))}
          </Space>
        }
      />
    </>
  )

  const renderStep3 = () => (
    <>
      <Form.Item
        name="org_id"
        label="Организация"
        rules={[
          {
            required: selectedRole !== 'superadmin',
            message: 'Выберите организацию',
          },
        ]}
      >
        <Select
          placeholder={selectedRole === 'superadmin' ? 'Для суперадмина можно не указывать' : 'Выберите организацию'}
          options={orgOptions}
          disabled={!isSuperadmin}
          allowClear={selectedRole === 'superadmin'}
          showSearch
          optionFilterProp="label"
        />
      </Form.Item>

      <Alert
        type="info"
        showIcon
        message="Что фиксирует этот шаг"
        description="Организация записывается в профиль пользователя платформы и становится его основным организационным контекстом. Группы вида mo<код ЛПУ> это больше не источник истины для организации и в обычном создании сотрудника не используются."
      />
    </>
  )

  const renderStep4 = () => (
    <>
      <Form.Item name="group_ids" label="Группы доступа">
        <Select
          mode="multiple"
          placeholder={
            selectedOrgId
              ? 'Выберите одну или несколько ACL-групп'
              : 'Сначала выберите организацию'
          }
          options={groupOptions}
          allowClear
          disabled={!selectedOrgId}
          notFoundContent="Для этой организации нет ACL-групп"
          optionFilterProp="label"
        />
      </Form.Item>

      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 12 }}
        message="Группы и данные — это отдельные вещи"
        description="Группы определяют, какие дашборды и BI-объекты увидит сотрудник. RLS-привязки определяют, какие строки данных он увидит внутри дашборда."
      />
      <Alert
        type="info"
        showIcon
        message="Что не делается автоматически"
        description="Этот wizard не назначает персональные RLS-привязки. Если сотруднику нужен доступ только к отдельным отделениям, службам или подразделениям, после создания откройте его карточку и сохраните RLS в разделе «RLS / данные»."
      />
    </>
  )

  // ── Success state ──
  const renderSuccess = () => {
    if (!createdUser) return null

    return (
      <Result
        status="success"
        icon={<CheckCircleOutlined />}
        title="Сотрудник добавлен"
        subTitle={
          <Space direction="vertical" size={4} style={{ textAlign: 'left', width: '100%' }}>
            <Paragraph style={{ margin: 0 }}>
              <Text strong>ФИО:</Text> {createdUser.full_name}
            </Paragraph>
            <Paragraph style={{ margin: 0 }}>
              <Text strong>Email:</Text> {createdUser.email}
            </Paragraph>
            <Paragraph style={{ margin: 0 }}>
              <Text strong>Организация:</Text> {createdUser.org_name}
            </Paragraph>
            <Paragraph style={{ margin: 0 }}>
              <Text strong>Роль:</Text> {createdUser.role_label}
            </Paragraph>
            {createdUser.group_names.length > 0 && (
              <Paragraph style={{ margin: 0 }}>
                <Text strong>Группы:</Text> {createdUser.group_names.join(', ')}
              </Paragraph>
            )}
            <Paragraph style={{ margin: 0 }}>
              <Text type="secondary">RLS при необходимости настраивается отдельно в карточке сотрудника.</Text>
            </Paragraph>
            {createdUser.requires_yandex_linking ? (
              <Alert
                type="warning"
                showIcon
                message="Для BI пользователь еще не считается готовым автоматически"
                description="У пользователя Yandex email (@yandex.ru / @ya.ru). Одного факта создания локального Zitadel account недостаточно: в карточке сотрудника проверьте, что Live login readiness стал `Yandex SSO ready`."
              />
            ) : null}
          </Space>
        }
        extra={
          <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <div>
              <Text strong style={{ display: 'block', marginBottom: 8 }}>
                Временный пароль (покажется только 1 раз):
              </Text>
              <Space.Compact style={{ width: '100%' }}>
                <Input
                  value={createdUser.temp_password}
                  readOnly
                  style={{ fontFamily: 'monospace', fontSize: 15 }}
                />
                <Button icon={<CopyOutlined />} onClick={handleCopyPassword}>
                  Копировать
                </Button>
              </Space.Compact>
            </div>

            <Space>
              {onOpenUserIdentity ? (
                <Button onClick={() => onOpenUserIdentity(createdUser.user_id, createdUser.email)}>
                  Открыть логин / identity
                </Button>
              ) : null}
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleCreateAnother}
              >
                Добавить ещё
              </Button>
              <Button onClick={handleClose}>Закрыть</Button>
            </Space>
          </Space>
        }
      />
    )
  }

  // ── Init form for org_admin ──
  const handleAfterOpenChange = (visible: boolean) => {
    if (visible && !isSuperadmin && authUser?.orgId) {
      form.setFieldValue('org_id', authUser.orgId)
      form.setFieldValue('role', 'analyst')
    }
  }

  // ── Footer buttons ──
  const renderFooter = () => {
    if (createdUser) return null // Success state has its own buttons

    return (
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <Button onClick={handleClose}>Отмена</Button>
        <Space>
          {currentStep > 0 && (
            <Button onClick={handlePrev} disabled={submitting}>
              Назад
            </Button>
          )}
          {currentStep < steps.length - 1 ? (
            <Button type="primary" onClick={handleNext}>
              Далее
            </Button>
          ) : (
            <Button type="primary" onClick={handleSubmit} loading={submitting}>
              Создать сотрудника
            </Button>
          )}
        </Space>
      </div>
    )
  }

  return (
    <Drawer
      title="Добавить сотрудника"
      open={open}
      onClose={handleClose}
      afterOpenChange={handleAfterOpenChange}
      width={520}
      footer={renderFooter()}
      destroyOnClose
    >
      {createdUser ? (
        renderSuccess()
      ) : (
        <Spin spinning={submitting}>
          <Steps
            current={currentStep}
            items={steps}
            size="small"
            style={{ marginBottom: 32 }}
          />

          <Form
            form={form}
            layout="vertical"
            requiredMark="optional"
            style={{ marginTop: 16 }}
          >
            <div style={{ display: currentStep === 0 ? 'block' : 'none' }}>
              {renderStep1()}
            </div>
            <div style={{ display: currentStep === 1 ? 'block' : 'none' }}>
              {renderStep2()}
            </div>
            <div style={{ display: currentStep === 2 ? 'block' : 'none' }}>
              {renderStep3()}
            </div>
            <div style={{ display: currentStep === 3 ? 'block' : 'none' }}>
              {renderStep4()}
            </div>
          </Form>
        </Spin>
      )}
    </Drawer>
  )
}
